import { QuantitySelectorComponent } from '@theme/component-quantity-selector';
import { parseIntOrDefault } from '@theme/utilities';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * Decreasing below the quantity-rule minimum sets quantity to 0 so cart-items can remove the line.
 *
 * @extends {QuantitySelectorComponent}
 */
export class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates quantity by a given step.
   * Decreasing below min removes the line (qty 0) instead of clamping at min.
   * @param {number} stepMultiplier - Positive for increase, negative for decrease
   */
  updateQuantity(stepMultiplier) {
    const { quantityInput } = this.refs;
    const { min, step, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    let newValue = value + step * stepMultiplier;

    if (stepMultiplier < 0 && newValue < min) {
      newValue = 0;
    } else {
      newValue = Math.min(effectiveMax ?? Infinity, Math.max(min, newValue));
    }

    quantityInput.value = newValue.toString();
    this.onQuantityChange();
    this.updateButtonStates();
  }

  /**
   * On blur: allow 0 to remove the line; otherwise snap like the product selector.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLInputElement)) return;

    event.preventDefault();
    const { quantityInput } = this.refs;
    const { min, step } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();
    const raw = parseInt(event.target.value, 10);

    if (raw === 0) {
      quantityInput.value = '0';
      this.onQuantityChange();
      this.updateButtonStates();
      return;
    }

    const quantity = Math.min(effectiveMax ?? Infinity, Math.max(min, parseIntOrDefault(event.target.value, min)));

    if ((quantity - min) % step !== 0) {
      quantityInput.value = quantity.toString();
      quantityInput.reportValidity();
      return;
    }

    quantityInput.value = quantity.toString();
    this.onQuantityChange();
    this.updateButtonStates();
  }

  /**
   * Updates button states based on current value and limits.
   * Minus stays enabled at min so the next click can remove the line.
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Cart buttons are always dynamically managed
    minusButton.disabled = value <= 0;
    plusButton.disabled = effectiveMax !== null && value >= effectiveMax;

    const isRemove = value > 0 && value <= min;
    minusButton.classList.toggle('quantity-minus--remove', isRemove);
    this.#syncMinusLabels(isRemove);
  }

  /**
   * @param {boolean} isRemove
   */
  #syncMinusLabels(isRemove) {
    const { minusButton } = this.refs;
    const decreaseLabel = minusButton.querySelector('.quantity-minus__label-decrease');
    const removeLabel = minusButton.querySelector('.quantity-minus__label-remove');

    if (decreaseLabel instanceof HTMLElement) {
      decreaseLabel.hidden = isRemove;
    }
    if (removeLabel instanceof HTMLElement) {
      removeLabel.hidden = !isRemove;
    }
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
